/** Accounting · Rentabilidade por Cliente faturado (tela 8.6). */
import { z } from 'zod'
import { dataNominal, uuid } from './comum.js'

export const setorDoCliente = z.enum(['Público', 'Privado'])
export type SetorDoCliente = z.infer<typeof setorDoCliente>

export const classeAbc = z.enum(['A', 'B', 'C'])
export type ClasseAbcApi = z.infer<typeof classeAbc>

export const filtrosDaRentabilidade = z.object({
  periodoDe: dataNominal,
  periodoAte: dataNominal,
  setor: setorDoCliente.optional(),
  /** Recorte textual após classificação ABC; a tela usa na exportação da busca local. */
  q: z.string().trim().optional(),
})
export type FiltrosDaRentabilidade = z.infer<typeof filtrosDaRentabilidade>

/**
 * A inadimplência de um cliente — o vencido de HOJE.
 *
 * Não prescreve (spec 05 §3, exceção 2): não depende do período escolhido, e por isso
 * viaja separada da cascata do período. Objeto ausente é "em dia"; vencido NULL
 * dentro do objeto é a conferência de origem legada (DS496), não saldo zero.
 */
export const inadimplenciaDoCliente = z.object({
  vencido: z.string().nullable(),
  diasAtraso: z.number().int(),
  parcelas: z.number().int(),
})
export type InadimplenciaDoCliente = z.infer<typeof inadimplenciaDoCliente>

/** Parcela sem parte Cliente: identifica o pedido, sem atribuir ao perfil/Anunciante. */
export const parcelaVencidaSemCliente = z.object({
  pedidoId: uuid,
  pedidoCodigo: z.string(),
  recebivelId: uuid,
  numeroParcela: z.number().int(),
  totalParcelas: z.number().int(),
  vencimento: dataNominal,
  diasAtraso: z.number().int(),
  valor: z.string().nullable(),
})
export type ParcelaVencidaSemCliente = z.infer<typeof parcelaVencidaSemCliente>

export const vencidoSemCliente = z.object({
  valor: z.string().nullable(),
  pedidos: z.number().int(),
  parcelas: z.number().int(),
  itens: z.array(parcelaVencidaSemCliente),
})

export const linhaDaRentabilidade = z.object({
  clienteId: uuid,
  clienteNome: z.string(),
  /**
   * Os anunciantes distintos do faturado no período, cada um com a empresa que o nome abre;
   * exclui o próprio Cliente (D-74). Ordem pelo nome. SUBSTITUI o antigo `anuncianteNome`
   * (os nomes unidos por "; "): um conceito, um lugar (D-136, DS-538).
   */
  anunciantes: z.array(z.object({ empresaId: uuid, nome: z.string() })),
  setor: setorDoCliente.nullable(),
  classe: classeAbc,
  /** Perda ou AGI zero, fora da concentração quando o total do período é positivo. */
  foraDaBaseAbc: z.boolean(),
  /**
   * AGI acumulado até este cliente, em FRAÇÃO (`0.4320` = 43,20%) — é o rótulo da barra.
   *
   * Fração porque é o que `dv_pct` guarda e o que a D-E (05/09/2026) fixou para todo o
   * fio. Quem multiplica por cem é a borda, uma vez, por `fracaoParaPercentual`. Enquanto
   * este endpoint misturava as duas unidades na MESMA resposta, cada tela tinha de
   * adivinhar qual era qual — e duas adivinharam errado por um fator de cem.
   */
  acumuladoPct: z.string(),
  faturamento: z.string(),
  repasse: z.string(),
  agi: z.string(),
  comissao: z.string(),
  margem: z.string(),
  /** Fração; `null` quando não houve faturamento — um 0% afirmaria margem nula. */
  margemPct: z.string().nullable(),
  inadimplencia: inadimplenciaDoCliente.nullable(),
  /**
   * Os números das NFs do período que somam o faturamento desta linha, em ordem —
   * é o subtítulo embaixo do nome do cliente. Sai da MESMA fonte que soma o valor,
   * e por isso a lista e o número ao lado dela não conseguem discordar sobre quais
   * notas entraram. Lista vazia só existiria sem faturamento, e sem faturamento não
   * há linha na tabela.
   *
   * Cada nota é `{id, numero}`: o `numero` é o rótulo de sempre (com a série quando o
   * número repete no cliente), e o `id` é o da NF, para o rótulo abrir a própria nota no
   * Livro (D-136, DS-538).
   */
  notasDoPeriodo: z.array(z.object({ id: uuid, numero: z.string() })),
})
export type LinhaDaRentabilidade = z.infer<typeof linhaDaRentabilidade>

export const kpisDaRentabilidade = z.object({
  agiTotal: z.string(),
  /** Fração, como todo percentual do fio (D-E). */
  margemMediaPct: z.string().nullable(),
  clientesA: z.number().int(),
  clientesAPct: z.string().nullable(),
  /**
   * O vencido da ENTIDADE, não da seleção: ele não prescreve e não responde a
   * período, busca nem setor (spec 05 §3, exceção 2).
   */
  inadimplencia: z.string().nullable(),
  inadimplenciaClientes: z.number().int(),
})
export type KpisDaRentabilidade = z.infer<typeof kpisDaRentabilidade>

export const totaisDaRentabilidade = z.object({
  clientes: z.number().int(),
  faturamento: z.string(),
  repasse: z.string(),
  agi: z.string(),
  comissao: z.string(),
  margem: z.string(),
  margemPct: z.string().nullable(),
  vencido: z.string().nullable(),
})
export type TotaisDaRentabilidade = z.infer<typeof totaisDaRentabilidade>

export const codigoDaPonte = z.enum(['faturamento', 'repasse', 'comissao', 'margem'])
export type CodigoDaPonte = z.infer<typeof codigoDaPonte>

/**
 * Uma linha da ponte de reconciliação com a DRE.
 *
 * As três primeiras fecham em zero por construção — a 8.6 e a 8.5 leem os MESMOS
 * fatos de `v_dre_lancamento`. A quarta não fecha, e não é defeito: a margem de
 * contribuição da DRE desconta também as deduções e os custos diretos, que a cascata
 * por cliente não carrega. `explicacao` nomeia essa diferença em vez de deixá-la como
 * um número solto que ninguém sabe ler.
 */
export const linhaDaPonte = z.object({
  codigo: codigoDaPonte,
  rotulo: z.string(),
  rotuloDre: z.string(),
  tela: z.string(),
  dre: z.string(),
  diferenca: z.string(),
  alerta: z.boolean(),
  explicacao: z.string().nullable(),
})
export type LinhaDaPonte = z.infer<typeof linhaDaPonte>

export const respostaDaRentabilidade = z.object({
  data: z.object({
    clientes: z.array(linhaDaRentabilidade),
    kpis: kpisDaRentabilidade,
    totais: totaisDaRentabilidade,
    ponte: z.array(linhaDaPonte),
    /** Alcance da entidade atual; independente do período, busca e setor da carteira. */
    vencidoSemCliente,
  }),
  meta: z.object({
    periodoDe: dataNominal,
    periodoAte: dataNominal,
    /** Os meses efetivamente comparados — o período é truncado a meses inteiros. */
    competenciaDe: dataNominal,
    competenciaAte: dataNominal,
    /**
     * A entidade que APUROU esta resposta.
     *
     * O `id` viaja junto do nome porque a `meta` é o que diz de qual recorte é o
     * número que está na mão — e a entidade é a parte do recorte que a tela não pode
     * conferir de outro jeito: ela chega pelo cabeçalho `X-DV-Entity`, e o seletor do
     * shell troca na hora, antes de a resposta nova existir. Sem o `id`, a tabela de
     * uma PJ fica embaixo do cabeçalho de outra até o pedido voltar.
     */
    entidade: z.object({ id: uuid, nome: z.string(), temDados: z.boolean() }),
    setor: setorDoCliente.nullable(),
    /** Universo do período ANTES dos filtros; resultado financeiro líquido permanece nos totais. */
    abc: z.object({ semConcentracao: z.boolean(), agiPositivo: z.string() }),
    /**
     * Faturamento e comissão que não puderam ser atribuídos a um Cliente. Aparece na
     * ponte em vez de sumir: dinheiro sem dono é informação, não erro a esconder.
     */
    naoAtribuido: z.string(),
    faixaDeAlertaPct: z.string(),
    moeda: z.literal('BRL'),
    podeExportar: z.boolean(),
  }),
})
export type RespostaDaRentabilidade = z.infer<typeof respostaDaRentabilidade>
